import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Users, 
  Clock, 
  CheckCircle2, 
  AlertTriangle, 
  Search, 
  ShieldCheck, 
  Copy, 
  Check, 
  Calendar, 
  ArrowUpRight,
  ExternalLink,
  Plus,
  RefreshCw,
  Gift,
  Lock,
  Trash2,
  XCircle,
  Edit2,
  Save,
  X,
  UserCheck,
  UserPlus
} from 'lucide-react';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../../components/common/Toast';
import { formatUzPhone, formatDate } from '../../utils/helpers';
import Modal from '../../components/common/Modal';
import PersonalSubscriptionView from '../../components/subscription/PersonalSubscriptionView';

export default function AdminSubscriptionsPage() {
  const { user } = useAuth();
  const isLawyer = user?.role === 'LAWYER';
  const isClient = user?.role === 'CLIENT' || user?.isClient;

  // For Lawyer or Client, render personal subscription view (no admin controls)
  if (isLawyer || isClient) {
    return <PersonalSubscriptionView user={user} />;
  }

  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState({ total: 0, pendingCount: 0, activeCount: 0, freeCount: 0, expiredCount: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [copiedCard, setCopiedCard] = useState(false);
  const [filterType, setFilterType] = useState('ALL'); // 'ALL', 'PENDING', 'CLIENT', 'LAWYER', 'FREE', 'EXPIRED'
  const [lastModifiedId, setLastModifiedId] = useState(null);

  // Card Settings State
  const [cardPan, setCardPan] = useState('5614 6814 2744 1112');
  const [cardHolder, setCardHolder] = useState('JIZZAX ADVOKATLARI');
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [editPan, setEditPan] = useState('');
  const [editHolder, setEditHolder] = useState('');
  const [savingCard, setSavingCard] = useState(false);

  // Grant Access Modal State
  const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);
  const [grantTargetType, setGrantTargetType] = useState('LAWYER'); // 'LAWYER' or 'CLIENT'
  const [grantTargetId, setGrantTargetId] = useState('');
  const [grantMode, setGrantMode] = useState('FREE'); // 'FREE' or '30_DAYS'
  const [grantNotes, setGrantNotes] = useState('');
  const [eligibleUsers, setEligibleUsers] = useState({ lawyers: [], clients: [] });
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [submittingGrant, setSubmittingGrant] = useState(false);

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const res = await api.get('/subscriptions/admin/list');
      if (res?.success) {
        setSubscriptions(res.subscriptions || []);
        if (res.stats) setStats(res.stats);
      }
    } catch (err) {
      showToast(err.message || 'Obunalarni yuklashda xatolik', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCardSettings = async () => {
    try {
      const res = await api.get('/subscriptions/pricing');
      if (res?.success) {
        if (res.cardPan) setCardPan(res.cardPan);
        if (res.cardHolder) setCardHolder(res.cardHolder);
      }
    } catch (err) {
      console.warn('Card settings load error:', err);
    }
  };

  useEffect(() => {
    loadSubscriptions();
    loadCardSettings();
  }, []);

  const handleCopyCard = () => {
    navigator.clipboard.writeText(cardPan.replace(/\s+/g, ''));
    setCopiedCard(true);
    showToast(`Karta raqami nusxalandi: ${cardPan}`, 'success');
    setTimeout(() => setCopiedCard(false), 2000);
  };

  const handleOpenEditCard = () => {
    setEditPan(cardPan);
    setEditHolder(cardHolder);
    setIsCardModalOpen(true);
  };

  const handleSaveCard = async (e) => {
    e.preventDefault();
    if (!editPan.trim()) {
      showToast('Karta raqamini kiriting', 'error');
      return;
    }
    setSavingCard(true);
    try {
      const res = await api.put('/subscriptions/admin/card-settings', {
        cardPan: editPan.trim(),
        cardHolder: editHolder.trim(),
      });
      if (res?.success) {
        setCardPan(res.cardPan);
        setCardHolder(res.cardHolder);
        setIsCardModalOpen(false);
        showToast('To‘lov kartasi muvaffaqiyatli yangilandi!', 'success');
      }
    } catch (err) {
      showToast(err.message || 'Kartani saqlashda xatolik', 'error');
    } finally {
      setSavingCard(false);
    }
  };

  // Open Grant Access Modal
  const handleOpenGrantModal = async () => {
    setIsGrantModalOpen(true);
    setLoadingEligible(true);
    try {
      const res = await api.get('/subscriptions/admin/eligible-users');
      if (res?.success) {
        const lawyers = res.lawyers || [];
        const clients = res.clients || [];
        setEligibleUsers({ lawyers, clients });
        if (grantTargetType === 'LAWYER' && lawyers.length > 0) {
          setGrantTargetId(String(lawyers[0].id));
        } else if (grantTargetType === 'CLIENT' && clients.length > 0) {
          setGrantTargetId(String(clients[0].id));
        }
      }
    } catch (err) {
      showToast(err.message || 'Foydalanuvchilarni yuklashda xatolik', 'error');
    } finally {
      setLoadingEligible(false);
    }
  };

  // Switch Target Type in Grant Modal
  const handleSwitchTargetType = (type) => {
    setGrantTargetType(type);
    if (type === 'LAWYER') {
      if (eligibleUsers.lawyers?.length > 0) {
        setGrantTargetId(String(eligibleUsers.lawyers[0].id));
      } else {
        setGrantTargetId('');
      }
    } else {
      if (eligibleUsers.clients?.length > 0) {
        setGrantTargetId(String(eligibleUsers.clients[0].id));
      } else {
        setGrantTargetId('');
      }
    }
  };

  // Submit Grant Access Form
  const handleGrantUserSubmit = async (e) => {
    e.preventDefault();
    if (!grantTargetId) {
      showToast('Foydalanuvchini tanlang', 'error');
      return;
    }
    setSubmittingGrant(true);
    try {
      const res = await api.post('/subscriptions/admin/grant-user-free', {
        targetType: grantTargetType,
        targetId: parseInt(grantTargetId, 10),
        mode: grantMode,
        notes: grantNotes.trim(),
      });
      if (res?.success) {
        showToast(res.message || 'Ruxsat muvaffaqiyatli berildi!', 'success');
        setIsGrantModalOpen(false);
        setGrantNotes('');
        loadSubscriptions();
      }
    } catch (err) {
      showToast(err.message || 'Ruxsat berishda xatolik', 'error');
    } finally {
      setSubmittingGrant(false);
    }
  };

  // Approve Pending Subscription
  const handleApprove = async (item) => {
    try {
      const res = await api.put(`/subscriptions/admin/${item.id}/approve`, {});
      if (res?.success) {
        showToast(res.message || 'To‘lov tasdiqlandi va faollashtirildi!', 'success');
        setLastModifiedId(item.id);
        loadSubscriptions();
      }
    } catch (err) {
      showToast(err.message || 'Tasdiqlashda xatolik', 'error');
    }
  };

  // Reject Pending Subscription
  const handleReject = async (item) => {
    if (!window.confirm(`Haqiqatan ham ${item.fullName} to‘lovini rad etmoqchimisiz?`)) return;
    try {
      const res = await api.put(`/subscriptions/admin/${item.id}/reject`, {});
      if (res?.success) {
        showToast(res.message || 'To‘lov rad etildi', 'info');
        setLastModifiedId(item.id);
        loadSubscriptions();
      }
    } catch (err) {
      showToast(err.message || 'Rad etishda xatolik', 'error');
    }
  };

  // Delete Subscription
  const handleDelete = async (item) => {
    if (!window.confirm(`Haqiqatan ham «${item.fullName}» obuna yozuvini butunlay o‘chirib tashlamoqchimisiz?`)) return;
    try {
      const res = await api.delete(`/subscriptions/admin/${item.id}`);
      if (res?.success) {
        showToast(res.message || 'Yozuv muvaffaqiyatli o‘chirildi', 'success');
        setSubscriptions(prev => prev.filter(s => s.id !== item.id));
        loadSubscriptions();
      }
    } catch (err) {
      showToast(err.message || 'O‘chirishda xatolik', 'error');
    }
  };

  // Grant Free access on existing record
  const handleGrantFree = async (item) => {
    try {
      const res = await api.put(`/subscriptions/admin/${item.id}/grant-free`, {});
      if (res?.success) {
        showToast(res.message || 'Bepul ruxsat berildi!', 'success');
        setLastModifiedId(item.id);
        loadSubscriptions();
      }
    } catch (err) {
      showToast(err.message || 'Ruxsat berishda xatolik', 'error');
    }
  };

  // Revoke Access on existing record
  const handleRevoke = async (item) => {
    if (!window.confirm(`Haqiqatan ham «${item.fullName}» ruxsatini bekor qilmoqchimisiz? Ushbu hisob darhol to‘xtatiladi va to‘lov talab qilinadi.`)) {
      return;
    }
    try {
      const res = await api.put(`/subscriptions/admin/${item.id}/revoke`, {});
      if (res?.success) {
        showToast(res.message || 'Ruxsat muvaffaqiyatli bekor qilindi!', 'warning');
        setLastModifiedId(item.id);
        loadSubscriptions();
      }
    } catch (err) {
      showToast(err.message || 'Ruxsatni bekor qilishda xatolik', 'error');
    }
  };

  // Extend 30 days
  const handleExtend = async (item) => {
    try {
      const res = await api.put(`/subscriptions/admin/${item.id}/extend-days`, { days: 30 });
      if (res?.success) {
        showToast(res.message, 'success');
        setLastModifiedId(item.id);
        loadSubscriptions();
      }
    } catch (err) {
      showToast(err.message || 'Muddatni uzaytirishda xatolik', 'error');
    }
  };

  const filteredList = subscriptions.filter((s) => {
    // Search query filter
    if (search.trim()) {
      const q = search.toLowerCase();
      const matches = (
        s.fullName?.toLowerCase().includes(q) ||
        s.phone?.includes(q) ||
        s.targetType?.toLowerCase().includes(q) ||
        s.notes?.toLowerCase().includes(q)
      );
      if (!matches) return false;
    }

    // Always keep the just-modified item visible in table so it never disappears on status change!
    if (lastModifiedId === s.id) return true;

    if (filterType === 'PENDING' && s.computedStatus !== 'PENDING') return false;
    if (filterType === 'ACTIVE' && s.computedStatus !== 'ACTIVE' && !s.isFreeExempt) return false;
    if (filterType === 'CLIENT' && s.targetType !== 'CLIENT') return false;
    if (filterType === 'LAWYER' && s.targetType !== 'LAWYER') return false;
    if (filterType === 'FREE' && !s.isFreeExempt) return false;
    if (filterType === 'EXPIRED' && s.computedStatus !== 'EXPIRED') return false;

    return true;
  });

  return (
    <div className="space-y-6 animate-admin-fade-in">
      
      {/* Official Card & Pricing Bar */}
      <div className="bg-[#090e18] border border-[#c5a880]/30 rounded-xl p-4 sm:p-5 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4 admin-card-interactive">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 rounded-xl bg-[#142034] border border-[#c5a880]/40 flex items-center justify-center text-[#c5a880] shrink-0">
            <CreditCard className="w-6 h-6" />
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold tracking-wider text-[#c5a880] block">
              Rasmiy To‘lov Qabul Qilish Kartasi (Click & Payme)
            </span>
            <div className="flex items-center space-x-2 mt-0.5 flex-wrap">
              <span className="font-mono text-lg font-bold text-slate-100">{cardPan}</span>
              <button
                type="button"
                onClick={handleCopyCard}
                className="px-2 py-0.5 rounded text-[11px] font-semibold bg-[#17243c] hover:bg-[#203254] text-[#c5a880] flex items-center transition-all active:scale-95 cursor-pointer"
                title="Nusxalash"
              >
                {copiedCard ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
              </button>
              <button
                type="button"
                onClick={handleOpenEditCard}
                className="px-2 py-0.5 rounded text-[11px] font-semibold bg-[#17243c] hover:bg-[#203254] text-blue-400 flex items-center transition-all active:scale-95 cursor-pointer"
                title="Kartani tahrirlash"
              >
                <Edit2 className="w-3 h-3 mr-1" />
                <span>Kartani o‘zgartirish</span>
              </button>
            </div>
            <span className="text-[11px] text-slate-400">
              Egasi: <strong className="text-slate-300">{cardHolder}</strong> | Mijozlar: <strong>35 000 so‘m/oy</strong> | Advokatlar: <strong>99 000 so‘m/oy</strong>
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-2 shrink-0">
          {/* Quick Grant Access Button */}
          <button
            type="button"
            onClick={handleOpenGrantModal}
            className="px-3.5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider shadow-sm transition-all active:scale-95 cursor-pointer flex items-center gap-1.5"
            title="Advokat yoki mijozga tekin ruxsat berish"
          >
            <Gift className="w-3.5 h-3.5" />
            <span>+ Bepul ruxsat berish</span>
          </button>

          <button
            type="button"
            onClick={loadSubscriptions}
            className="px-3 py-2 rounded-lg bg-[#142034] hover:bg-[#1b2b46] border border-slate-700 text-xs font-semibold text-slate-200 flex items-center cursor-pointer transition-all active:scale-95"
            title="Jadvalni yangilash"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            <span>Yangilash</span>
          </button>
        </div>
      </div>

      {/* KPI Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-3.5 sm:p-4 shadow-2xs admin-card-interactive">
          <div className="flex items-center justify-between text-slate-500 mb-1">
            <span className="text-xs font-medium">Jami Obunalar</span>
            <Users className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-bold font-mono text-slate-900">{stats.total}</div>
          <span className="text-[10px] text-slate-400">Tizimdagi barcha hisoblar</span>
        </div>

        <div className="bg-amber-50 rounded-lg border border-amber-300 p-3.5 sm:p-4 shadow-2xs admin-card-interactive">
          <div className="flex items-center justify-between text-amber-700 mb-1">
            <span className="text-xs font-bold">⏳ Kutilayotganlar</span>
            <Clock className="w-4 h-4 text-amber-600 animate-spin" />
          </div>
          <div className="text-2xl font-bold font-mono text-amber-900">{stats.pendingCount || 0}</div>
          <span className="text-[10px] text-amber-700">Tasdiqlash kutilayotgan to‘lovlar</span>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-3.5 sm:p-4 shadow-2xs admin-card-interactive">
          <div className="flex items-center justify-between text-emerald-600 mb-1">
            <span className="text-xs font-medium">Faol Obunachilar</span>
            <CheckCircle2 className="w-4 h-4" />
          </div>
          <div className="text-2xl font-bold font-mono text-emerald-700">{stats.activeCount}</div>
          <span className="text-[10px] text-slate-400">Oylik to‘lovi faol bo‘lganlar</span>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-3.5 sm:p-4 shadow-2xs admin-card-interactive">
          <div className="flex items-center justify-between text-purple-600 mb-1">
            <span className="text-xs font-medium">Tekin Ruxsatlar</span>
            <Gift className="w-4 h-4" />
          </div>
          <div className="text-2xl font-bold font-mono text-purple-700">{stats.freeCount}</div>
          <span className="text-[10px] text-slate-400">Admin ruxsati bilan bepul</span>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-3.5 sm:p-4 shadow-2xs col-span-2 lg:col-span-1 admin-card-interactive">
          <div className="flex items-center justify-between text-rose-600 mb-1">
            <span className="text-xs font-medium">Muddati Tugaganlar</span>
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div className="text-2xl font-bold font-mono text-rose-700">{stats.expiredCount}</div>
          <span className="text-[10px] text-slate-400">Ruxsati to‘xtatilganlar</span>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4 shadow-2xs flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5 w-full sm:w-auto">
          {[
            { id: 'ALL', label: `Barchasi (${stats.total})` },
            { id: 'PENDING', label: `⏳ Kutilmoqda (${stats.pendingCount || 0})`, highlight: (stats.pendingCount || 0) > 0 },
            { id: 'ACTIVE', label: `Faollar (${stats.activeCount})` },
            { id: 'FREE', label: `Tekin ruxsat (${stats.freeCount})` },
            { id: 'CLIENT', label: 'Mijozlar (35k)' },
            { id: 'LAWYER', label: 'Advokatlar (99k)' },
            { id: 'EXPIRED', label: 'Tugaganlar' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilterType(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                filterType === tab.id
                  ? 'bg-[#091322] text-[#c5a880]'
                  : tab.highlight
                  ? 'bg-amber-100 text-amber-900 border border-amber-300 animate-pulse font-bold'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="relative w-full sm:w-72">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Ism, telefon raqam izlash..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:border-[#c5a880] focus:outline-none"
          />
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-240px)] overflow-y-auto">
          <table className="w-full text-left text-xs min-w-[980px] border-collapse">
            <thead className="sticky top-0 z-20 bg-slate-50 text-slate-600 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-2.5 px-3">Foydalanuvchi & Telefon</th>
                <th className="py-2.5 px-3 whitespace-nowrap">Tarif & To‘lov</th>
                <th className="py-2.5 px-3 whitespace-nowrap">Qolgan Muddat</th>
                <th className="py-2.5 px-3 whitespace-nowrap">Holati</th>
                <th className="sticky right-0 z-30 bg-slate-100 py-2.5 px-3 text-right whitespace-nowrap shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.06)] border-b border-slate-200 min-w-[330px] w-[330px]">
                  Boshqaruv
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-slate-400">Yuklanmoqda...</td>
                </tr>
              ) : filteredList.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-slate-400">Hech qanday obuna yozuvi topilmadi</td>
                </tr>
              ) : (
                filteredList.map((item) => {
                  const isFree = item.isFreeExempt;
                  const isPending = item.computedStatus === 'PENDING';
                  const isRejected = item.computedStatus === 'REJECTED';
                  const isExpired = !isFree && item.computedStatus === 'EXPIRED';
                  const hasActiveAccess = isFree || (!isExpired && !isPending && !isRejected);
                  const isLastModified = item.id === lastModifiedId;

                  return (
                    <tr 
                      key={item.id} 
                      className={`group transition-all duration-300 ${
                        isLastModified 
                          ? 'bg-emerald-50/80 ring-2 ring-emerald-500/80 shadow-md' 
                          : isPending 
                          ? 'bg-amber-50/60 hover:bg-amber-50' 
                          : 'bg-white hover:bg-slate-50'
                      }`}
                    >
                      
                      {/* User Info */}
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-900 flex items-center gap-1.5 flex-wrap">
                          <span>{item.fullName}</span>
                          {item.user?.username && (
                            <span className="text-[10px] text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.2 rounded font-mono font-medium">
                              @{item.user.username}
                            </span>
                          )}
                          {item.client?.username && (
                            <span className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.2 rounded font-mono font-medium">
                              @{item.client.username}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] font-mono text-slate-500 mt-0.5">
                          {item.phone ? formatUzPhone(item.phone) : 'Telefon kiritilmagan'}
                        </div>
                        {item.notes && (
                          <div className="text-[10px] text-slate-400 mt-0.5 max-w-xs truncate" title={item.notes}>
                            Izoh: {item.notes}
                          </div>
                        )}
                      </td>

                      {/* Tariff & Payment */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${
                            item.targetType === 'LAWYER'
                              ? 'bg-purple-100 text-purple-800 border border-purple-200'
                              : 'bg-blue-100 text-blue-800 border border-blue-200'
                          }`}>
                            {item.targetType === 'LAWYER' ? 'Advokat (99k)' : 'Mijoz (35k)'}
                          </span>
                          <span className="font-mono font-bold text-slate-800 text-xs">
                            {item.amount?.toLocaleString('uz-UZ')} UZS
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-slate-500">
                          <span className="font-semibold">{item.paymentMethod || 'ADMIN_GRANT'}</span>
                          {item.receiptUrl && (
                            <a
                              href={item.receiptUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-1.5 py-0.2 rounded font-bold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100"
                            >
                              <ExternalLink className="w-2.5 h-2.5 mr-0.5" /> Chek
                            </a>
                          )}
                        </div>
                      </td>

                      {/* Remaining Days */}
                      <td className="py-3 px-4 whitespace-nowrap font-mono tabular-nums">
                        {isPending ? (
                          <span className="inline-flex items-center text-amber-700 font-bold text-[11px]">
                            <Clock className="w-3.5 h-3.5 mr-1 text-amber-600 animate-spin" />
                            Kutilmoqda
                          </span>
                        ) : isFree ? (
                          <span className="inline-flex items-center text-purple-700 font-bold text-[11px]">
                            <Gift className="w-3.5 h-3.5 mr-1 text-purple-500" />
                            Cheksiz Bepul
                          </span>
                        ) : isExpired ? (
                          <span className="inline-flex items-center text-rose-600 font-bold text-[11px]">
                            <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                            Muddati tugagan
                          </span>
                        ) : (
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3.5 h-3.5 text-emerald-500" />
                            <span className="font-bold text-emerald-700">{item.daysLeft} kun qoldi</span>
                          </div>
                        )}
                        <span className="text-[10px] text-slate-400 block mt-0.5">
                          {isPending ? 'To‘lov tasdiqlanmagan' : isFree ? 'Cheksiz muddat' : `Tugash: ${formatDate(item.endDate)}`}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          isPending
                            ? 'bg-amber-100 text-amber-900 border border-amber-300 font-extrabold'
                            : isFree
                            ? 'bg-purple-100 text-purple-800 border border-purple-300'
                            : isRejected
                            ? 'bg-gray-100 text-gray-700 border border-gray-300'
                            : isExpired
                            ? 'bg-rose-100 text-rose-800 border border-rose-300'
                            : 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                        }`}>
                          {isPending 
                            ? '⏳ Kutilmoqda' 
                            : isFree 
                            ? 'Tekin Ruxsat' 
                            : isRejected 
                            ? 'Rad etilgan' 
                            : isExpired 
                            ? 'To‘lov kerak' 
                            : 'Faol'}
                        </span>
                      </td>

                      {/* Management Buttons */}
                      <td className={`sticky right-0 z-10 py-2.5 px-3 text-right whitespace-nowrap shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.06)] transition-colors min-w-[330px] w-[330px] ${isPending ? 'bg-amber-50/90 group-hover:bg-amber-50' : 'bg-white group-hover:bg-slate-50'}`}>
                        <div className="inline-flex items-center justify-end space-x-1.5">
                          
                          {/* If PENDING: show Approve and Reject buttons */}
                          {isPending ? (
                            <>
                              <button
                                type="button"
                                onClick={() => handleApprove(item)}
                                className="px-2.5 py-1 text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded transition-colors inline-flex items-center cursor-pointer shadow-xs active:scale-95"
                                title="To‘lovni tasdiqlash va 30 kun berish"
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                                <span>Tasdiqlash</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleReject(item)}
                                className="px-2 py-1 text-[11px] font-medium text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-300 rounded transition-colors inline-flex items-center cursor-pointer active:scale-95"
                                title="To‘lovni rad etish"
                              >
                                <XCircle className="w-3.5 h-3.5 mr-1" />
                                <span>Rad etish</span>
                              </button>
                            </>
                          ) : (
                            <>
                              {/* If user currently has active or free access: Show Red REVOKE button */}
                              {hasActiveAccess ? (
                                <button
                                  type="button"
                                  onClick={() => handleRevoke(item)}
                                  className="px-2.5 py-1 text-[11px] font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-300 rounded transition-colors inline-flex items-center cursor-pointer shadow-2xs active:scale-95"
                                  title="Ruxsatni darhol bekor qilish va hisobni to‘xtatish"
                                >
                                  <XCircle className="w-3.5 h-3.5 mr-1 text-rose-600" />
                                  <span>Ruxsatni bekor qilish</span>
                                </button>
                              ) : (
                                /* If expired/rejected: Show Green GRANT FREE button */
                                <button
                                  type="button"
                                  onClick={() => handleGrantFree(item)}
                                  className="px-2.5 py-1 text-[11px] font-bold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded transition-colors inline-flex items-center cursor-pointer shadow-2xs active:scale-95"
                                  title="Cheksiz bepul ruxsat berish"
                                >
                                  <Gift className="w-3.5 h-3.5 mr-1 text-emerald-600" />
                                  <span>Tekin ruxsat berish</span>
                                </button>
                              )}

                              {/* If active paid but not permanent free: also offer to switch to permanent free */}
                              {!isFree && hasActiveAccess && (
                                <button
                                  type="button"
                                  onClick={() => handleGrantFree(item)}
                                  className="px-2 py-1 text-[11px] font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded transition-colors inline-flex items-center cursor-pointer active:scale-95"
                                  title="Cheksiz bepulga o‘tkazish"
                                >
                                  <Gift className="w-3.5 h-3.5 mr-1 text-purple-500" />
                                  <span>Tekin qilish</span>
                                </button>
                              )}

                              {/* Extend 30 days */}
                              <button
                                type="button"
                                onClick={() => handleExtend(item)}
                                className="px-2 py-1 text-[11px] font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-300 rounded transition-colors inline-flex items-center cursor-pointer active:scale-95 shadow-2xs"
                                title="30 kun qo‘shish"
                              >
                                <Plus className="w-3.5 h-3.5 mr-0.5 text-[#c5a880]" />
                                <span>30 kun</span>
                              </button>
                            </>
                          )}

                          {/* Delete Entry - Prominent Red Action Button, Always Visible */}
                          <button
                            type="button"
                            onClick={() => handleDelete(item)}
                            className="p-1.5 text-rose-600 hover:text-white bg-rose-50 hover:bg-rose-600 border border-rose-200 rounded-md transition-all cursor-pointer shadow-xs active:scale-95 ml-1 shrink-0"
                            title="O‘chirish"
                            aria-label="O‘chirish"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>

                        </div>
                      </td>

                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Grant Access Modal */}
      {isGrantModalOpen && (
        <Modal
          isOpen={isGrantModalOpen}
          onClose={() => setIsGrantModalOpen(false)}
          title="Foydalanuvchiga Bepul / Muddatli Ruxsat Berish"
        >
          <form onSubmit={handleGrantUserSubmit} className="space-y-4">
            
            {/* Target Type Selector */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Kimga ruxsat bermoqchisiz? *
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => handleSwitchTargetType('LAWYER')}
                  className={`py-2 px-3 rounded-lg text-xs font-bold border flex items-center justify-center gap-1.5 transition-colors cursor-pointer ${
                    grantTargetType === 'LAWYER'
                      ? 'bg-purple-50 border-purple-500 text-purple-800'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <UserCheck className="w-4 h-4 text-purple-600" />
                  <span>Advokatlar</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchTargetType('CLIENT')}
                  className={`py-2 px-3 rounded-lg text-xs font-bold border flex items-center justify-center gap-1.5 transition-colors cursor-pointer ${
                    grantTargetType === 'CLIENT'
                      ? 'bg-blue-50 border-blue-500 text-blue-800'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Users className="w-4 h-4 text-blue-600" />
                  <span>Mijozlar</span>
                </button>
              </div>
            </div>

            {/* User Select Dropdown */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                {grantTargetType === 'LAWYER' ? 'Advokatni tanlang' : 'Mijozni tanlang'} *
              </label>
              {loadingEligible ? (
                <div className="p-3 bg-slate-50 border rounded text-xs text-slate-500 text-center">
                  Foydalanuvchilar ro‘yxati yuklanmoqda...
                </div>
              ) : (
                <select
                  required
                  value={grantTargetId}
                  onChange={(e) => setGrantTargetId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-xs font-medium bg-white focus:border-[#c5a880] focus:outline-none"
                >
                  <option value="">-- Tanlang --</option>
                  {grantTargetType === 'LAWYER' ? (
                    eligibleUsers.lawyers?.map((lawyer) => (
                      <option key={lawyer.id} value={lawyer.id}>
                        {lawyer.fullName} {lawyer.username ? `(@${lawyer.username})` : ''} {lawyer.phone ? `— ${formatUzPhone(lawyer.phone)}` : ''}
                      </option>
                    ))
                  ) : (
                    eligibleUsers.clients?.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.fullName} {client.username ? `(@${client.username})` : ''} {client.phone ? `— ${formatUzPhone(client.phone)}` : ''}
                      </option>
                    ))
                  )}
                </select>
              )}
            </div>

            {/* Access Mode */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Ruxsat berish turi *
              </label>
              <div className="space-y-2">
                <label className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${
                  grantMode === 'FREE' ? 'bg-purple-50/70 border-purple-400' : 'bg-white border-slate-200 hover:bg-slate-50'
                }`}>
                  <input
                    type="radio"
                    name="grantMode"
                    value="FREE"
                    checked={grantMode === 'FREE'}
                    onChange={() => setGrantMode('FREE')}
                    className="mt-0.5 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="ml-2.5">
                    <span className="block text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <Gift className="w-3.5 h-3.5 text-purple-600" />
                      <span>Cheksiz Bepul Ruxsat (10 yil)</span>
                      <span className="text-[10px] px-1.5 py-0.2 rounded bg-purple-100 text-purple-700 font-semibold">Tavsiya</span>
                    </span>
                    <span className="block text-[11px] text-slate-500 mt-0.5">
                      Foydalanuvchiga to‘lovsiz, doimiy cheksiz kirish va xizmatlardan foydalanish huquqi beriladi.
                    </span>
                  </div>
                </label>

                <label className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${
                  grantMode === '30_DAYS' ? 'bg-emerald-50/70 border-emerald-400' : 'bg-white border-slate-200 hover:bg-slate-50'
                }`}>
                  <input
                    type="radio"
                    name="grantMode"
                    value="30_DAYS"
                    checked={grantMode === '30_DAYS'}
                    onChange={() => setGrantMode('30_DAYS')}
                    className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div className="ml-2.5">
                    <span className="block text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-emerald-600" />
                      <span>30 Kunlik Faol Muddat</span>
                    </span>
                    <span className="block text-[11px] text-slate-500 mt-0.5">
                      Bugundan boshlab to‘liq 30 kunlik faol muddat beriladi. 30 kundan so‘ng yana to‘lov talab qilinadi.
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {/* Optional Notes */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Izoh (ixtiyoriy)
              </label>
              <input
                type="text"
                value={grantNotes}
                onChange={(e) => setGrantNotes(e.target.value)}
                placeholder="Masalan: Bosh admin tomonidan maxsus ruxsat berildi"
                className="w-full px-3 py-2 border rounded-lg text-xs focus:border-[#c5a880] focus:outline-none"
              />
            </div>

            <div className="flex justify-end space-x-2 pt-3 border-t">
              <button
                type="button"
                onClick={() => setIsGrantModalOpen(false)}
                className="px-4 py-2 border rounded-lg text-xs text-slate-600 hover:bg-slate-50 cursor-pointer"
              >
                Bekor qilish
              </button>
              <button
                type="submit"
                disabled={submittingGrant || !grantTargetId}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold flex items-center cursor-pointer disabled:opacity-50 transition-colors shadow-xs"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                <span>{submittingGrant ? 'Tasdiqlanmoqda...' : 'Ruxsatni Faollashtirish'}</span>
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Card Modal */}
      {isCardModalOpen && (
        <Modal
          isOpen={isCardModalOpen}
          onClose={() => setIsCardModalOpen(false)}
          title="To‘lov Qabul Qilish Karta Ma’lumotlarini O‘zgartirish"
        >
          <form onSubmit={handleSaveCard} className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
              Ushbu karta Click, Payme va saytdagi barcha obuna oynalarida to‘lov qabul qilish kartasi sifatida ko‘rinadi.
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Karta Raqami (PAN) *
              </label>
              <input
                type="text"
                required
                value={editPan}
                onChange={(e) => setEditPan(e.target.value)}
                placeholder="5614 6814 2744 1112"
                className="w-full px-3 py-2 border rounded font-mono text-sm focus:border-[#c5a880] focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Karta Egasi (Ism Familiya yoki Tashkilot)
              </label>
              <input
                type="text"
                value={editHolder}
                onChange={(e) => setEditHolder(e.target.value)}
                placeholder="JIZZAX ADVOKATLARI"
                className="w-full px-3 py-2 border rounded text-sm focus:border-[#c5a880] focus:outline-none"
              />
            </div>

            <div className="flex justify-end space-x-2 pt-3 border-t">
              <button
                type="button"
                onClick={() => setIsCardModalOpen(false)}
                className="px-4 py-2 border rounded text-xs text-slate-600 hover:bg-slate-50 cursor-pointer"
              >
                Bekor qilish
              </button>
              <button
                type="submit"
                disabled={savingCard}
                className="px-4 py-2 bg-[#091322] hover:bg-[#132238] text-[#c5a880] rounded text-xs font-semibold flex items-center cursor-pointer disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5 mr-1.5" />
                <span>{savingCard ? 'Saqlanmoqda...' : 'Saqlash'}</span>
              </button>
            </div>
          </form>
        </Modal>
      )}

    </div>
  );
}
